import React, { useRef, useState, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { PapiDeviation } from '@wix/da-papi-types';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import {
  ThemeOverride,
  ThemeSurface,
} from '@wix/da-react-context/pkg/ThemeContext';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import noop from '../../../../utils/noop';
import { getDeviationEntityId } from '../../../../redux/normalizr/schemas/deviation';
import { useSubscriptionTier } from '../../../../utils/hooks/useSubscriptionTier';
import { getDeviationResourceType, getDeviationUrl } from '../../../_helpers';

import FeedCardHeader from '../FeedCardHeader';
import ExpandableWrapper from '../ExpandableWrapper';
import FeedCardActionBar from '../FeedCardActionBar';
import FeedCardCommentThread from '../FeedCardCommentThread';
import ScrollToNextPostButton from '../ScrollToNextPostButton';
import { POST_HEIGHT_TO_SHOW_SCROLL_TO_NEXT } from '../../constants';
import {
  BiData,
  NextPostBiEvent,
  ReadMoreClickBiEvent,
} from '@wix/da-bi/pkg/events';

import s from './FeedCardLayout.scss';

export interface Props {
  deviation: PapiDeviation;
  /** any additional menu actions for the '...' in the top right of the header */
  extraActions?: NullableDropdownItem[];
  className?: string;
  children?: React.ReactNode;
  /** does the content section expand for very long content
   *  and have a skip button that scrolls to the next post */
  isExpandable?: boolean;
  withFaveControl?: boolean;
  withCommentControl?: boolean;
  withWatchOrSubscribeButton?: boolean;

  // because commissions
  expansionButton?: React.ReactNode;
  /** Force the toggle button to be shown even if the content is not
   *  tall enough to cause the wrapper to be collapsible */
  forceShowExpansionToggle?: boolean;
  // because commissions
  additionalActionbarActions?: React.ReactNode;
  /** max height in px of middle content section when not expanded */
  collapsedContentHeight?: number;
  /** called when the overflows have been measured, applied, and the card is at it's final height */
  onLayoutReady?: () => void;

  showReportModal?: () => void;
  deleteDeviation?: () => void;
  showGenericNotification?: (params: any) => void;
}

const FeedCardLayout: React.FC<Props> = ({
  deviation,
  extraActions,
  className,
  children,
  isExpandable,
  withFaveControl,
  withCommentControl,
  withWatchOrSubscribeButton,
  expansionButton,
  forceShowExpansionToggle,
  additionalActionbarActions,
  collapsedContentHeight,
  onLayoutReady = noop,
  showReportModal = noop,
  deleteDeviation = noop,
  showGenericNotification = noop,
}) => {
  const { t } = useTranslation();

  const { needsSubscription, isSubscribedButBlocked, subscriptionsTabUrl } =
    useSubscriptionTier(deviation);
  const actionBlockedBySubscription =
    needsSubscription || isSubscribedButBlocked;

  const [isCommentThreadOpen, setCommentThreadOpen] = useState(false);
  const openCommentThread = useCallback(() => setCommentThreadOpen(true), []);

  // track when the overflow is measured so we can turn off disableScrollRestoration in duperbrowse
  const [isOverflowMeasured, setIsOverflowMeasured] = useState(false);
  const onOverflowMeasured = useCallback(() => {
    setIsOverflowMeasured(true);
    onLayoutReady();
  }, [onLayoutReady]);
  // if it's not expandable, signal that's it's already ready
  useEffect(() => {
    !isExpandable && onOverflowMeasured();
  }, [isExpandable, onOverflowMeasured]);

  // have a sticky button appear when a long enough post is expanded allowing
  // the user to skip over it, also have it disappear if the end of this post is
  // scrolled into view
  const endOfThisPostRef = useRef<HTMLDivElement | null>(null);
  const [inViewRef, endAlreadyInView] = useInView();
  const setEndOfThisPostRef = useCallback(
    node => {
      // sets '.current' as normal but also hijack because we need to call inViewRef
      endOfThisPostRef.current = node;
      inViewRef(node);
    },
    [inViewRef]
  );

  const contentContainerRef = useRef<HTMLDivElement>(null);
  const expandedPostHeight = contentContainerRef.current?.offsetHeight || 0;
  const hasScrollToNextButton =
    isExpandable && expandedPostHeight >= POST_HEIGHT_TO_SHOW_SCROLL_TO_NEXT;
  return (
    <div
      className={classnames(
        s['root'],
        'ds-surface-secondary',
        actionBlockedBySubscription && s['in-unsubscribed-tier'],
        className
      )}
    >
      <ThemeOverride themeSurface={ThemeSurface.SECONDARY}>
        <FeedCardHeader
          className={s['header']}
          deviation={deviation}
          extraActions={extraActions}
          // scroll restoration must wait until the overflows are determined,
          // otherwise things shift around after the scroll
          disableScrollRestoration={!isOverflowMeasured}
          withWatchOrSubscribeButton={withWatchOrSubscribeButton}
          showReportModal={showReportModal}
          deleteDeviation={deleteDeviation}
        />

        <Section>
          {isExpandable ? (
            <ExpandableWrapper
              contentContainerRef={contentContainerRef}
              collapsedHeight={collapsedContentHeight}
              toggleButtonBiData={BiData<ReadMoreClickBiEvent>({
                evid: 116,
                typeid: getDeviationResourceType(deviation),
                itemid: deviation.deviationId,
                creatorid: deviation.author?.useridUuid ?? '',
              })}
              toggleButton={expansionButton}
              overflowCacheKey={`feed-card-${getDeviationEntityId(deviation)}`}
              onExpansion={openCommentThread}
              onOverflowMeasured={onOverflowMeasured}
              allowExpansion={!actionBlockedBySubscription}
              forceShowToggle={forceShowExpansionToggle}
              toggleButtonContents={
                actionBlockedBySubscription
                  ? t('deviationview.feed.subscribeToReadMore')
                  : t('deviationview.feed.readmore')
              }
              toggleButtonHref={
                actionBlockedBySubscription
                  ? subscriptionsTabUrl
                  : getDeviationUrl(deviation)
              }
            >
              {children}
            </ExpandableWrapper>
          ) : (
            children
          )}
        </Section>

        <div className={s['footer']}>
          <FeedCardActionBar
            className={s['action-bar']}
            deviation={deviation}
            withFaveControl={withFaveControl}
            withCommentControl={withCommentControl}
            additionalActions={additionalActionbarActions}
            openCommentThread={openCommentThread}
          />
          {!actionBlockedBySubscription && (
            <FeedCardCommentThread
              deviation={deviation}
              isThreadOpen={isCommentThreadOpen}
              openCommentThread={openCommentThread}
              showGenericNotification={showGenericNotification}
            />
          )}
        </div>

        {hasScrollToNextButton && (
          <ScrollToNextPostButton
            buttonClassName={classnames(
              s['scroll-to-next-button'],
              !endAlreadyInView && s['visible']
            )}
            isDisplayed={hasScrollToNextButton}
            scrollTarget={endOfThisPostRef}
            biData={BiData<NextPostBiEvent>({
              evid: 119,
              typeid: getDeviationResourceType(deviation),
              itemid: deviation.deviationId,
              creatorid: deviation.author?.useridUuid,
            })}
          />
        )}
        {hasScrollToNextButton && <div ref={setEndOfThisPostRef} />}
      </ThemeOverride>
    </div>
  );
};
FeedCardLayout.displayName = 'FeedCardLayout';

export default FeedCardLayout;
